import axios from "axios";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { adminAuthService } from "@/services/admin-auth.service";
import type { AdminAuthResponse } from "@/types/oauth";
import { clearSessionData } from "./clearSessionData";

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  roles: ("SuperAdmin" | "BillingAdmin" | "SupportAdmin")[];
  permissions: string[];
}

interface AdminAuthState {
  user: AdminUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  loginWithOAuth: (authResponse: AdminAuthResponse) => void;
  logout: () => void;
  setUser: (user: AdminUser) => void;
  setTokens: (token: string, refreshToken: string) => void;
  refreshAccessToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
  checkAndRefreshToken: () => Promise<void>;
}

/**
 * The refresh that is currently in flight, shared by every caller in this tab.
 *
 * Module scope rather than store state on purpose: it holds a Promise, which must never reach
 * localStorage through `persist`.
 *
 * Without it a page load fires two refreshes at once - `main.tsx` checks the stored token on boot
 * while the first screen's queries are already 401-ing into the response interceptor - and the
 * server ROTATES refresh tokens (`RefreshAdminTokenCommandHandler` revokes the old one as it
 * issues the new pair). The two calls therefore send the same token, the loser is told it is
 * revoked, and `refreshAccessToken` reads that 401 as a dead session and logs the operator out.
 * Reproduced: two POSTs, both carrying R1, session gone.
 */
let refreshInFlight: Promise<void> | null = null;

/**
 * Resolves once the refresh running right now has settled, and at once when none is running.
 *
 * Its failure is not reported here: whoever started the refresh handles it, and a caller that
 * only waited then proceeds with whatever session is left.
 */
export const waitForRefreshInFlight = async (): Promise<void> => {
  if (!refreshInFlight) {
    return;
  }

  try {
    await refreshInFlight;
  } catch {
    // Handled by the caller that started the refresh.
  }
};

/** The `persist` key below. Read directly so a refresh always uses the newest stored pair. */
const AUTH_STORAGE_KEY = "admin-auth-storage";

/**
 * The lock name every tab of this console competes for before it refreshes.
 *
 * `refreshInFlight` only covers one tab, and the server rotates: an operator with the console
 * open twice had one tab rotate the pair while the other still held the previous one in memory,
 * so that tab's next 401 sent a token the server had already revoked and the operator was thrown
 * back to /login mid-task. Observed on production for several operators.
 */
const REFRESH_LOCK = "admin-auth-refresh";

/**
 * True when the server answered that this session is over (401, or 403 for an operator whose
 * admin role was removed) — as opposed to not answering properly at all: a 5xx, a timeout or a
 * dropped connection during a deploy says nothing about the token. Only the first may end the
 * session, because ending it now ends it in every open tab.
 */
const isSessionRefused = (error: unknown): boolean => {
  let status: number | undefined;
  if (axios.isAxiosError(error)) {
    status = error.response?.status;
  } else if (typeof error === "object" && error !== null && "statusCode" in error) {
    // APIError from the shared client — not imported, since `lib/api` imports this store.
    const statusCode = (error as { statusCode?: unknown }).statusCode;
    status = typeof statusCode === "number" ? statusCode : undefined;
  }

  return status === 401 || status === 403;
};

// Utility function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

/**
 * Reads the token pair straight out of the persisted session, which is what another tab's
 * rotation wrote. The in-memory copy of a tab that has been open across that rotation is stale.
 */
const readPersistedTokens = (): {
  token: string | null;
  refreshToken: string | null;
} => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return { token: null, refreshToken: null };
    }

    const parsed = JSON.parse(raw);
    const state = parsed?.state ?? parsed;
    return {
      token: state?.token ?? null,
      refreshToken: state?.refreshToken ?? null,
    };
  } catch {
    // A blocked or corrupt store is not a reason to refuse the refresh — the caller falls back
    // to the in-memory pair, which is what this store did before the lock existed.
    return { token: null, refreshToken: null };
  }
};

/**
 * Runs the refresh while holding a lock shared by every tab on this origin.
 *
 * The Web Locks API needs a secure context; where it is missing the call runs unserialised,
 * which is exactly the behaviour this console had before.
 */
const withRefreshLock = async <T>(run: () => Promise<T>): Promise<T> => {
  if (!navigator.locks) {
    return run();
  }

  // Awaited rather than returned straight through: `LockManager.request` is typed to resolve
  // with whatever the callback returns, so handing it back unwrapped reads as Promise<Promise<T>>.
  const result = await navigator.locks.request(REFRESH_LOCK, run);
  return result;
};

/**
 * Clears this tab's session without telling the server — shared by `logout`, which does tell it,
 * and by a sign-out in another tab, which already has.
 */
const endSessionLocally = (): void => {
  useAdminAuthStore.setState({
    user: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
  });

  // The nulls above are persisted, so the auth entry clears itself. Everything else the
  // operator accumulated did not — including an impersonation token for a customer's
  // account, which nothing anywhere removed.
  clearSessionData();
};

/**
 * Keeps this tab's session in step with the other tabs of this console.
 *
 * `persist` writes localStorage but never reads it again after boot, and the `storage` event
 * fires only in the OTHER tabs. Two cases:
 * - a rotation: adopt the new pair, since the one this tab holds has just been revoked;
 * - a sign-out: end the session here too, and the route guard sends this tab to /login. This
 *   used to be ignored, so a tab left open kept working as if the operator never signed out.
 */
export const installAuthStorageSync = (): void => {
  window.addEventListener("storage", (event) => {
    if (event.key !== AUTH_STORAGE_KEY) {
      return;
    }

    const { token, refreshToken } = readPersistedTokens();
    if (!token || !refreshToken) {
      // The entry is gone or holds nulls: another tab signed out. Its own logout already asked
      // the server to revoke the token, so this tab only clears its copy.
      if (useAdminAuthStore.getState().isAuthenticated) {
        endSessionLocally();
      }
      return;
    }

    const state = useAdminAuthStore.getState();
    if (state.token === token && state.refreshToken === refreshToken) {
      return;
    }

    useAdminAuthStore.setState({ token, refreshToken });
  });
};

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const data = await adminAuthService.login({ email, password });

          set({
            user: data.user,
            token: data.token,
            refreshToken: data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithOAuth: (authResponse: AdminAuthResponse) => {
        set({
          user: {
            id: authResponse.user.id,
            email: authResponse.user.email,
            fullName: authResponse.user.fullName,
            roles: authResponse.user.roles as (
              | "SuperAdmin"
              | "BillingAdmin"
              | "SupportAdmin"
            )[],
            permissions: authResponse.user.permissions,
          },
          token: authResponse.token,
          refreshToken: authResponse.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        const { token, refreshToken } = get();
        if (token) {
          // The refresh token is what the server revokes. Logout used to send nothing, so the
          // token stayed good for 30 days and a second tab kept renewing its session with it.
          adminAuthService.logout(refreshToken).catch(() => {
            // Ignore logout errors
          });
        }

        endSessionLocally();
      },

      setUser: (user: AdminUser) => set({ user }),

      setTokens: (token: string, refreshToken: string) =>
        set({ token, refreshToken, isAuthenticated: true }),

      refreshAccessToken: async () => {
        // Join the refresh already running instead of starting a second one — see refreshInFlight.
        if (refreshInFlight) {
          return refreshInFlight;
        }

        refreshInFlight = withRefreshLock(async () => {
          // Read the store only after the lock is held: another tab may have rotated while this
          // call waited for it, and the pair captured before waiting would already be revoked.
          const persisted = readPersistedTokens();

          // Adopting that tab's pair costs no request and, more importantly, does not rotate
          // again — a second rotation would strand whichever tab is next. The pair has to DIFFER
          // from this tab's: an unchanged, unexpired token means the 401 came from something a
          // refresh still has to be attempted for.
          const rotatedElsewhere =
            persisted.token !== null &&
            persisted.refreshToken !== null &&
            persisted.token !== get().token &&
            !isTokenExpired(persisted.token);

          if (rotatedElsewhere) {
            set({
              token: persisted.token,
              refreshToken: persisted.refreshToken,
            });
            return;
          }

          const refreshToken = persisted.refreshToken ?? get().refreshToken;
          if (!refreshToken) {
            get().logout();
            // Throw rather than return: the 401 interceptor awaits this call and reads a normal
            // return as a successful refresh, then retries the original request with `Bearer null`
            // — another 401, and no session cleanup. There is nothing to refresh with here.
            throw new Error("No refresh token available");
          }

          try {
            const data = await adminAuthService.refreshToken({ refreshToken });
            set({
              token: data.token,
              refreshToken: data.refreshToken, // Update with new refresh token (rotation)
            });
          } catch (error) {
            console.error("Token refresh failed:", error);
            if (isSessionRefused(error)) {
              get().logout();
            }
            throw error;
          }
        });

        try {
          await refreshInFlight;
        } finally {
          // Cleared by the caller that started it; anyone who joined already holds the promise.
          refreshInFlight = null;
        }
      },

      checkAuth: async () => {
        const { token } = get();
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        try {
          await adminAuthService.verify();
        } catch (error) {
          // A 401 has already been through the refresh in the response interceptor, which ends
          // the session itself when the token is refused; a failure that is not a refusal
          // leaves the operator signed in.
          if (isSessionRefused(error)) {
            get().logout();
          }
        }
      },

      checkAndRefreshToken: async () => {
        const { token, refreshToken } = get();

        if (!token || !refreshToken) {
          return;
        }

        if (isTokenExpired(token)) {
          await get().refreshAccessToken();
        }
      },
    }),
    {
      name: "admin-auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
